import React, { useState, useRef, useEffect } from "react";

import workspaceLogo from "../../images/logo/workway_1000.png";

const Education = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false); // 전체화면 상태
  const presentationRef = useRef(null);

  // 전체화면 변경 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  // 전체화면 토글 함수
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      const element = presentationRef.current;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // 키보드 단축키 처리
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isFullscreen) {
        switch (e.key) {
          case "ArrowRight":
          case " ":
            nextSlide();
            break;
          case "ArrowLeft":
            prevSlide();
            break;
          case "Escape":
            // Escape는 자동으로 전체화면을 종료하므로 여기서 처리할 필요 없음
            break;
          case "f":
          case "F":
            toggleFullscreen();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen, currentSlide]); // currentSlide 종속성 추가

  // 슬라이드 컨텐츠
  const slides = [
    // 첫 번째 슬라이드 (메인 제목 슬라이드)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex flex-col justify-center items-center py-10 flex-grow">
        {/* 로고 */}
        <div className="mb-12">
          <img
            alt="Google Workspace Logo"
            className="logo"
            src={workspaceLogo}
          />
        </div>

        {/* 제목 */}
        <h1 className="title text-5xl mb-6 text-center max-w-4xl">
          Google Workspace 활용 스마트 워크 교육
        </h1>

        {/* 구분선 */}
        <div className="w-24 h-1 bg-blue-500 mb-8"></div>

        {/* 부제목 */}
        <p className="subtitle text-2xl text-center max-w-3xl mb-10">
          출근부터 퇴근까지! 김OO 대리의 스마트한 하루와 함께 알아보는 Google
          Workspace 업무 혁신 사례
        </p>

        {/* 아이콘 영역 */}
        <div className="flex justify-center space-x-8 mb-8">
          <span className="text-blue-500">
            <i className="fas fa-envelope fa-2x"></i>
          </span>
          <span className="text-red-500">
            <i className="fas fa-calendar-alt fa-2x"></i>
          </span>
          <span className="text-yellow-500">
            <i className="fas fa-comments fa-2x"></i>
          </span>
          <span className="text-green-500">
            <i className="fas fa-folder fa-2x"></i>
          </span>
          <span className="text-blue-500">
            <i className="fas fa-file-alt fa-2x"></i>
          </span>
          <span className="text-red-500">
            <i className="fas fa-video fa-2x"></i>
          </span>
        </div>
      </div>

      {/* 하단 정보 */}
      <div className="bottom-info p-6">
        <div className="flex justify-between">
          <div>워크웨이 (WorkWay)</div>
          <div>Google Workspace 교육</div>
        </div>
      </div>
    </div>,
    // 두 번째 슬라이드 (교육 개요 및 목표)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">교육 개요 및 목표</h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">교육 목적</h2>
            <p className="content text-lg">
              <span className="highlight">Google Workspace</span>의 주요
              도구(Gmail, Calendar, Chat, Drive, Docs/Sheets/Slides, Meet)를
              유기적으로 활용하여 <span className="highlight">업무 효율성</span>
              과 <span className="highlight">협업</span>을 극대화하는 방법을
              체득합니다.
            </p>
          </div>

          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">교육 대상</h2>
            <ul className="content text-lg list-disc pl-6 space-y-2">
              <li>신입사원</li>
              <li>Google Workspace 활용에 익숙하지 않은 기존 직원</li>
              <li>업무 생산성과 협업 능력 향상을 원하는 모든 구성원</li>
            </ul>
          </div>

          <div>
            <h2 className="font-bold text-xl mb-3">학습 방식</h2>
            <p className="content text-lg">
              마케팅팀 김OO 대리의 '2025년 4분기 신제품 런칭 캠페인' 프로젝트
              진행 사례를 통해 <span className="highlight">실무 중심</span>으로
              학습합니다.
            </p>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="flex space-x-6 mb-8">
            <div className="icon-box blue-box">
              <i className="fas fa-rocket fa-3x"></i>
            </div>
            <div className="icon-box red-box">
              <i className="fas fa-users fa-3x"></i>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 w-full">
            <h3 className="font-bold text-lg mb-3 text-center">핵심 가치</h3>
            <div className="flex justify-between">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                  <i className="fas fa-clock text-blue-500 fa-lg"></i>
                </div>
                <span className="text-sm font-medium">시간 절약</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-2">
                  <i className="fas fa-handshake text-red-500 fa-lg"></i>
                </div>
                <span className="text-sm font-medium">협업 강화</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-2">
                  <i className="fas fa-link text-yellow-600 fa-lg"></i>
                </div>
                <span className="text-sm font-medium">통합 환경</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                  <i className="fas fa-chart-line text-green-500 fa-lg"></i>
                </div>
                <span className="text-sm font-medium">생산성 향상</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">2 / 20</div>
      </div>
    </div>,
    // 세 번째 슬라이드 (학습 흐름 미리보기)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">학습 흐름 미리보기</h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠: 타임라인 */}
      <div className="px-16 py-6 flex-grow">
        <div className="mb-8">
          <p className="content text-lg">
            김OO 대리의 하루를 통해 Google Workspace 도구들이 어떻게 유기적으로
            연결되어 업무 효율성을 높이는지 함께 알아봅니다.
          </p>
        </div>

        {/* 타임라인 */}
        <div className="flex justify-between items-start mt-10">
          {/* 단계 1: 출근 및 업무 시작 */}
          <div className="timeline-item w-1/4 px-4 text-center">
            <div className="timeline-circle blue-circle">
              <i className="fas fa-briefcase fa-2x"></i>
            </div>
            <h3 className="font-bold text-lg mb-2">오전 9:00</h3>
            <h4 className="font-bold text-base mb-3">출근 및 업무 시작</h4>
            <p className="text-sm mb-3">오늘 일정 파악 및 이메일 확인</p>
            <div className="mt-3">
              <span className="tool-badge badge-blue">Gmail</span>
              <span className="tool-badge badge-red">Calendar</span>
              <span className="tool-badge badge-yellow">Chat</span>
            </div>
          </div>

          {/* 단계 2: 내부 회의 */}
          <div className="timeline-item w-1/4 px-4 text-center">
            <div className="timeline-circle red-circle">
              <i className="fas fa-users fa-2x"></i>
            </div>
            <h3 className="font-bold text-lg mb-2">오전 10:00</h3>
            <h4 className="font-bold text-base mb-3">내부 회의</h4>
            <p className="text-sm mb-3">화상회의와 실시간 회의록 작성</p>
            <div className="mt-3">
              <span className="tool-badge badge-red">Meet</span>
              <span className="tool-badge badge-blue">Docs</span>
              <span className="tool-badge badge-green">Slides</span>
            </div>
          </div>

          {/* 단계 3: 문서 작업 및 협업 */}
          <div className="timeline-item w-1/4 px-4 text-center">
            <div className="timeline-circle yellow-circle">
              <i className="fas fa-file-alt fa-2x"></i>
            </div>
            <h3 className="font-bold text-lg mb-2">오후 1:00</h3>
            <h4 className="font-bold text-base mb-3">문서 작업 및 협업</h4>
            <p className="text-sm mb-3">데이터 분석과 보고서 작성</p>
            <div className="mt-3">
              <span className="tool-badge badge-green">Drive</span>
              <span className="tool-badge badge-blue">Docs</span>
              <span className="tool-badge badge-yellow">Sheets</span>
            </div>
          </div>

          {/* 단계 4: 업무 마무리 및 퇴근 준비 */}
          <div className="timeline-item w-1/4 px-4 text-center">
            <div className="timeline-circle green-circle">
              <i className="fas fa-check-circle fa-2x"></i>
            </div>
            <h3 className="font-bold text-lg mb-2">오후 5:30</h3>
            <h4 className="font-bold text-base mb-3">업무 마무리 및 퇴근</h4>
            <p className="text-sm mb-3">업무 요약 및 내일 준비</p>
            <div className="mt-3">
              <span className="tool-badge badge-yellow">Chat</span>
              <span className="tool-badge badge-blue">Tasks</span>
              <span className="tool-badge badge-red">Gmail</span>
            </div>
          </div>
        </div>

        {/* 하단 설명 */}
        <div className="mt-16 p-5 bg-gray-50 rounded-lg">
          <h3 className="font-bold text-lg mb-3">핵심 학습 포인트</h3>
          <div className="flex justify-between">
            <div className="w-1/2 pr-4">
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Gmail 라벨, 필터, 다시 알림으로 효율적인 이메일 관리</li>
                <li>캘린더 일정 공유 및 첨부파일 활용</li>
                <li>Chat 스페이스를 통한 팀 소통</li>
                <li>Meet 화상회의와 화면 공유</li>
              </ul>
            </div>
            <div className="w-1/2 pl-4">
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Drive 공유 드라이브로 체계적인 파일 관리</li>
                <li>Docs, Sheets, Slides 실시간 공동 작업</li>
                <li>Tasks를 활용한 할 일 관리</li>
                <li>Gmail 예약 발송으로 업무 자동화</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">3 / 20</div>
      </div>
    </div>,
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">시나리오 소개</h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">주인공</h2>
            <p className="content text-lg">
              <span className="highlight">마케팅팀 김OO 대리</span>는 입사 2년
              차 마케팅 실무자로, 디지털 마케팅과 브랜드 커뮤니케이션을 담당하고
              있습니다. 업무 특성상 여러 부서와 협업이 필요하며, 효율적인
              프로젝트 관리가 중요합니다.
            </p>
          </div>

          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">프로젝트 배경</h2>
            <p className="content text-lg">
              회사의 야심찬{" "}
              <span className="highlight">2025년 4분기 신제품 런칭 캠페인</span>
              이 한창 진행 중입니다. 이 프로젝트는 온/오프라인 마케팅, 광고
              제작, 이벤트 기획, 영업 전략 수립 등 전사적 협업이 필요한 중요
              프로젝트입니다.
            </p>
          </div>

          <div>
            <h2 className="font-bold text-xl mb-3">김OO 대리의 역할</h2>
            <ul className="content text-lg list-disc pl-6 space-y-2">
              <li>프로젝트 실무 책임자</li>
              <li>팀원 및 외부 협력사와 커뮤니케이션 관리</li>
              <li>마케팅 콘텐츠 기획 및 승인 프로세스 관리</li>
              <li>일정 및 예산 관리</li>
            </ul>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="profile-card w-full mb-6">
            <div className="p-6 bg-white">
              <div className="flex justify-between items-center mb-4">
                <div className="flex space-x-4">
                  <div className="icon-box blue-box">
                    <i className="fas fa-user-tie fa-2x"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">김OO 대리</h3>
                    <p className="text-sm text-gray-500">마케팅팀</p>
                  </div>
                </div>
                <span className="badge badge-blue">주 캐릭터</span>
              </div>
            </div>

            <div className="profile-info p-5">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <i className="fas fa-calendar-alt text-blue-500"></i>
                </div>
                <div>
                  <p className="text-sm font-medium">2023년 입사</p>
                  <p className="text-xs text-gray-500">2년 차 마케팅 실무자</p>
                </div>
              </div>

              <div className="flex items-center mb-3">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <i className="fas fa-tasks text-red-500"></i>
                </div>
                <div>
                  <p className="text-sm font-medium">신제품 런칭 캠페인</p>
                  <p className="text-xs text-gray-500">프로젝트 실무 책임자</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <i className="fas fa-bullhorn text-green-500"></i>
                </div>
                <div>
                  <p className="text-sm font-medium">디지털 마케팅 전문가</p>
                  <p className="text-xs text-gray-500">
                    브랜드 커뮤니케이션 담당
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-4">
            <div className="icon-box red-box">
              <i className="fas fa-ad fa-2x"></i>
            </div>
            <div className="icon-box yellow-box">
              <i className="fas fa-lightbulb fa-2x"></i>
            </div>
            <div className="icon-box green-box">
              <i className="fas fa-chart-line fa-2x"></i>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">4 / 20</div>
      </div>
    </div>,
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex flex-col justify-center items-center py-12 flex-grow">
        {/* 시간 표시 */}
        <div className="circle-icon blue-circle mb-8">
          <div className="text-center">
            <span className="text-2xl font-bold">09:00</span>
            <div className="text-sm font-medium mt-1">AM</div>
          </div>
        </div>

        {/* 제목 */}
        <h1 className="title text-5xl mb-6 text-center">
          🌟 STEP 1. 오전 9시 : 출근과 업무 시작
        </h1>

        {/* 구분선 */}
        <div className="w-24 h-1 bg-blue-500 mb-8"></div>

        {/* 부제목 */}
        <p className="subtitle text-2xl text-center max-w-3xl mb-10">
          오늘 일정 한눈에 확인하고, 받은 메일을 효율적으로 분류하는 스마트 업무
          첫걸음!
        </p>

        {/* 도구 뱃지 */}
        <div className="mt-4">
          <span className="tool-badge badge-blue">
            <i className="fas fa-envelope mr-2"></i>Gmail
          </span>
          <span className="tool-badge badge-red">
            <i className="fas fa-calendar-alt mr-2"></i>Calendar
          </span>
          <span className="tool-badge badge-yellow">
            <i className="fas fa-comments mr-2"></i>Chat
          </span>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">5 / 20</div>
      </div>
    </div>,
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent" style={{ backgroundColor: "#EA4335" }}></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">Gmail 라벨 자동 분류/필터링</h1>
        <div className="w-16 h-1 bg-red-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <div className="step-circle mr-3">1</div>
              <h2 className="font-bold text-xl">라벨 생성하기</h2>
            </div>
            <div className="step-box pl-8 ml-3 pb-4">
              <p className="content text-lg mb-2">
                메일을 효과적으로 분류할 수 있는 라벨을 만듭니다:
              </p>
              <ul className="content text-lg list-disc pl-6 space-y-1">
                <li>
                  <span className="highlight" style={{ color: "#EA4335" }}>
                    [4분기 캠페인]
                  </span>{" "}
                  - 프로젝트 관련 메일
                </li>
                <li>
                  <span className="highlight" style={{ color: "#EA4335" }}>
                    [거래처]
                  </span>{" "}
                  - 외부 업체 커뮤니케이션
                </li>
                <li>
                  <span className="highlight" style={{ color: "#EA4335" }}>
                    [내부공유]
                  </span>{" "}
                  - 팀 내부 정보 공유
                </li>
              </ul>
              <p className="content text-sm mt-2 italic">
                왼쪽 패널 &gt; 더보기 &gt; 새 라벨 만들기
              </p>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center mb-4">
              <div className="step-circle mr-3">2</div>
              <h2 className="font-bold text-xl">필터 설정하기</h2>
            </div>
            <div className="step-box pl-8 ml-3 pb-4">
              <p className="content text-lg mb-2">
                특정 조건의 메일에 자동으로 라벨을 적용합니다:
              </p>
              <ul className="content text-lg list-disc pl-6 space-y-1">
                <li>검색창 상단 화살표 클릭 → 조건 지정</li>
                <li>특정 발신자(예: ad_agency@example.com)</li>
                <li>'필터 만들기' → '다음 라벨 적용' 선택</li>
              </ul>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center mb-4">
              <div className="step-circle mr-3">3</div>
              <h2 className="font-bold text-xl">자동분류함 활용하기</h2>
            </div>
            <div className="step-box pl-8 ml-3">
              <p className="content text-lg mb-2">
                효율적인 메일 관리를 위한 자동분류 설정:
              </p>
              <ul className="content text-lg list-disc pl-6 space-y-1">
                <li>설정 → '받은편지함 유형' → '자동분류함' 선택</li>
                <li>중요 메일 상단 배치, 라벨별 섹션 구성 가능</li>
                <li>라벨 기반으로 별도 받은편지함 만들기</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="bg-gray-50 rounded-lg p-6 w-full mb-6">
            <h3 className="font-bold text-lg mb-4 text-center">실무 활용 팁</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mr-3">
                  <i className="fas fa-tags text-red-500"></i>
                </div>
                <div>
                  <p className="font-medium text-base mb-1">계층형 라벨 구조</p>
                  <p className="text-sm text-gray-600">
                    프로젝트명/하위카테고리 형태로 구성해 체계적 관리
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mr-3">
                  <i className="fas fa-filter text-red-500"></i>
                </div>
                <div>
                  <p className="font-medium text-base mb-1">중복 라벨 활용</p>
                  <p className="text-sm text-gray-600">
                    하나의 메일에 여러 라벨 적용 가능 (예: [거래처][중요])
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mr-3">
                  <i className="fas fa-star text-red-500"></i>
                </div>
                <div>
                  <p className="font-medium text-base mb-1">
                    별표와 라벨 함께 사용
                  </p>
                  <p className="text-sm text-gray-600">
                    최우선 처리 메일은 라벨과 별표 동시 적용
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-5 w-full">
            <div className="flex items-center mb-3">
              <span className="badge badge-red mr-2">학습 포인트 1</span>
              <h4 className="font-bold">라벨과 필터로 수신함 자동화</h4>
            </div>
            <p className="text-sm text-gray-700">
              김OO 대리는 '[4분기 캠페인]', '[거래처]', '[내부공유]' 등 미리
              설정해 둔 라벨로 이메일을 자동 분류하여 중요한 메일을 놓치지 않고
              확인합니다.
            </p>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">6 / 20</div>
      </div>
    </div>,
    // 일곱 번째 슬라이드 (Gmail 다시 알림(Snooze) 활용)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">Gmail 다시 알림(Snooze) 활용</h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">
              다시 알림(Snooze) 기능이란?
            </h2>
            <p className="content text-lg">
              바로 처리하기 어려운 메일을{" "}
              <span className="highlight">나중에 필요한 시점</span>에 상기시켜
              주는 기능입니다. 설정한 시간에 해당 메일이 받은편지함 상단에 다시
              표시됩니다.
            </p>
          </div>

          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">다시 알림 설정 방법</h2>
            <div className="step-box blue-border p-4 mb-4">
              <div className="flex items-start">
                <div className="rounded-full bg-blue-500 text-white w-6 h-6 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                  1
                </div>
                <p className="content">
                  처리가 필요하지만 지금 바로 할 수 없는 이메일을 선택합니다.
                </p>
              </div>
            </div>

            <div className="step-box blue-border p-4 mb-4">
              <div className="flex items-start">
                <div className="rounded-full bg-blue-500 text-white w-6 h-6 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                  2
                </div>
                <p className="content">
                  이메일 오른쪽에 있는 다시 알림{" "}
                  <i className="fas fa-clock text-blue-500"></i> 아이콘을
                  클릭합니다.
                </p>
              </div>
            </div>

            <div className="step-box blue-border p-4">
              <div className="flex items-start">
                <div className="rounded-full bg-blue-500 text-white w-6 h-6 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                  3
                </div>
                <p className="content">
                  원하는 시간을 선택합니다 (오늘 저녁, 내일 아침, 다음 주 등)
                  또는 직접 날짜와 시간 지정도 가능합니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="icon-box blue-box mb-8">
            <i className="fas fa-clock fa-3x"></i>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 w-full mb-6">
            <h3 className="font-bold text-lg mb-4 text-center">
              자주 사용하는 다시 알림 시간
            </h3>
            <div className="flex flex-wrap justify-center">
              <div className="time-tag">
                <i className="fas fa-sun mr-2"></i>내일 오전
              </div>
              <div className="time-tag">
                <i className="fas fa-moon mr-2"></i>오늘 저녁
              </div>
              <div className="time-tag">
                <i className="fas fa-calendar-week mr-2"></i>다음 주
              </div>
              <div className="time-tag">
                <i className="fas fa-coffee mr-2"></i>점심 시간
              </div>
              <div className="time-tag">
                <i className="fas fa-hourglass-half mr-2"></i>2시간 후
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-5 w-full">
            <h3 className="font-bold text-lg mb-3 text-green-800">
              <i className="fas fa-lightbulb text-yellow-500 mr-2"></i>김OO
              대리의 활용 사례
            </h3>
            <ul className="text-green-800 space-y-2">
              <li>
                <i className="fas fa-check-circle text-green-600 mr-2"></i>
                오후에 답변할 거래처 문의 메일
              </li>
              <li>
                <i className="fas fa-check-circle text-green-600 mr-2"></i>다음
                회의 전에 검토할 보고서
              </li>
              <li>
                <i className="fas fa-check-circle text-green-600 mr-2"></i>내일
                아침 확인할 마케팅 통계 자료
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">7 / 20</div>
      </div>
    </div>,
    // 여덟 번째 슬라이드 (Google 캘린더로 일정·첨부파일·참석자 한눈에!)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          Google 캘린더로 일정·첨부파일·참석자 한눈에!
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="feature-card">
            <h2 className="font-bold text-xl mb-2 flex items-center">
              <span className="inline-block w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                <i className="fas fa-calendar-day text-blue-500"></i>
              </span>
              오늘의 전체 일정 확인
            </h2>
            <p className="content text-base">
              Gmail 우측 상단 앱 런처를 통해{" "}
              <span className="highlight">캘린더로 바로 이동</span>하여 오늘
              예정된 '10시 4분기 캠페인 주간회의'와 '3시 외부 광고대행사 미팅'
              등 모든 일정을 한눈에 확인합니다.
            </p>
          </div>

          <div className="feature-card">
            <h2 className="font-bold text-xl mb-2 flex items-center">
              <span className="inline-block w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                <i className="fas fa-file-alt text-red-500"></i>
              </span>
              회의록, 안건 등 첨부자료 사전 숙지
            </h2>
            <p className="content text-base">
              캘린더 일정에{" "}
              <span className="highlight">첨부된 Google Docs 회의록</span>과{" "}
              <span className="highlight">Google Slides 안건 자료</span>를
              클릭하여 내용을 사전에 검토하고 숙지합니다. 일정 상세 정보에서
              관련 문서 바로 열기가 가능합니다.
            </p>
          </div>

          <div className="feature-card">
            <h2 className="font-bold text-xl mb-2 flex items-center">
              <span className="inline-block w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">
                <i className="fas fa-users text-green-500"></i>
              </span>
              참석자 확인을 통한 사전 준비
            </h2>
            <p className="content text-base">
              일정에 표시된{" "}
              <span className="highlight">참석자 목록을 미리 확인</span>하여
              의견 교환이 필요한 동료나 협조가 필요한 부서 담당자를 파악하고,
              회의 전 필요한 자료를 준비합니다.
            </p>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400 mt-4">
            <p className="text-sm flex items-start">
              <i className="fas fa-lightbulb text-yellow-500 mr-2 mt-1"></i>
              <span>
                <span className="font-medium">실무 TIP</span>: 중요한 회의는
                알림 시간을 '10분 전'으로 설정하여 사전 준비 시간을 확보하세요.
                반복 일정은 '매주' 또는 '매월' 옵션으로 자동 생성할 수 있습니다.
              </span>
            </p>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center space-y-6">
          <div className="flex space-x-6">
            <div className="icon-box blue-box">
              <i className="fas fa-calendar-alt fa-3x"></i>
            </div>
            <div className="icon-box green-box">
              <i className="fas fa-video fa-3x"></i>
            </div>
          </div>

          <div className="calendar-preview w-full">
            <div className="calendar-header flex justify-between items-center">
              <div className="text-lg font-medium">2025년 9월 10일</div>
              <div className="flex space-x-2">
                <i className="fas fa-search"></i>
                <i className="fas fa-cog"></i>
              </div>
            </div>
            <div className="bg-white p-4">
              <div className="calendar-event meeting-event">
                <div className="flex justify-between">
                  <span className="font-medium">4분기 캠페인 주간회의</span>
                  <span className="text-sm">10:00 - 11:00</span>
                </div>
                <div className="text-sm mt-1 flex items-center">
                  <i className="fas fa-paperclip mr-1 text-gray-500"></i>{" "}
                  회의록.docs, 안건.slides
                  <i className="fas fa-users ml-3 mr-1 text-gray-500"></i>{" "}
                  참석자 5명
                </div>
              </div>
              <div className="calendar-event deadline-event">
                <div className="flex justify-between">
                  <span className="font-medium">외부 광고대행사 미팅</span>
                  <span className="text-sm">15:00 - 16:30</span>
                </div>
                <div className="text-sm mt-1 flex items-center">
                  <i className="fas fa-map-marker-alt mr-1 text-gray-500"></i>{" "}
                  Google Meet
                  <i className="fas fa-users ml-3 mr-1 text-gray-500"></i>{" "}
                  참석자 8명
                </div>
              </div>
              <div className="text-center mt-3">
                <button className="bg-blue-50 text-blue-700 font-medium px-4 py-2 rounded-full text-sm">
                  <i className="fas fa-plus mr-1"></i> 일정 추가
                </button>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg w-full">
            <h3 className="font-bold text-lg mb-2 text-blue-800">
              캘린더 연동의 강점
            </h3>
            <div className="flex justify-between">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-1">
                  <i className="fas fa-bell text-blue-500"></i>
                </div>
                <span className="text-xs font-medium text-center">
                  자동
                  <br />
                  알림
                </span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-1">
                  <i className="fas fa-share-alt text-blue-500"></i>
                </div>
                <span className="text-xs font-medium text-center">
                  일정
                  <br />
                  공유
                </span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-1">
                  <i className="fas fa-sync text-blue-500"></i>
                </div>
                <span className="text-xs font-medium text-center">
                  자동
                  <br />
                  업데이트
                </span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-1">
                  <i className="fas fa-mobile-alt text-blue-500"></i>
                </div>
                <span className="text-xs font-medium text-center">
                  모바일
                  <br />
                  동기화
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">8 / 20</div>
      </div>
    </div>,
    // 아홉 번째 슬라이드 (Google Chat로 팀원과 소통)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">Google Chat로 팀원과 소통 시작</h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">스페이스(Space) 활용하기</h2>
            <div className="step-box chat-step">
              <p className="content text-lg mb-2">
                <span className="highlight">'4분기 캠페인 프로젝트'</span>{" "}
                스페이스에 접속하여 팀원들과 실시간 소통
              </p>
              <div className="flex items-center space-x-3 mt-3 mb-3">
                <i className="fas fa-rocket text-blue-500"></i>
                <p className="content">
                  프로젝트별 스페이스로 업무 관련 대화를 집중
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-layer-group text-blue-500"></i>
                <p className="content">
                  중요 정보는 고정 메시지로 등록하여 빠른 참조
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">
              공지사항 및 중요 정보 확인
            </h2>
            <div className="step-box chat-step">
              <p className="content text-lg">
                간밤에 팀장님이 공유한 중요 공지사항이 있는지 확인하고{" "}
                <span className="highlight">@멘션</span>으로 나를 태그한 메시지
                우선 확인
              </p>
              <div className="flex items-center space-x-3 mt-4">
                <span className="chat-badge">TIP</span>
                <p className="content">
                  읽지 않은 메시지는 굵은 글씨로 표시되고, 나를 언급한 메시지는
                  특별 표시됨
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-bold text-xl mb-3">효과적인 메시지 작성법</h2>
            <div className="step-box chat-step">
              <div className="flex items-center space-x-3 mb-3">
                <i className="fas fa-thumbs-up text-green-500"></i>
                <p className="content">
                  <strong>간결하게:</strong> "좋은 아침입니다! 오늘 회의 자료
                  미리 확인했습니다."
                </p>
              </div>
              <div className="flex items-center space-x-3 mb-3">
                <i className="fas fa-thumbs-up text-green-500"></i>
                <p className="content">
                  <strong>이모지 활용:</strong> 시각적 단서로 메시지 강조 👍 🔔
                  ⭐
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-thumbs-up text-green-500"></i>
                <p className="content">
                  <strong>스레드 활용:</strong> 주제별 대화를 나눠 정리
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="bg-gray-50 rounded-lg p-6 w-full mb-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                <i className="fas fa-user text-green-600"></i>
              </div>
              <div>
                <p className="font-medium">4분기 캠페인 프로젝트</p>
                <p className="text-xs text-gray-500">팀원 8명</p>
              </div>
            </div>
            <div className="chat-message">
              <p className="font-medium text-sm">박과장</p>
              <p>
                여러분, 오늘 회의에서 논의할 4분기 마케팅 전략 문서 링크
                공유드립니다.
              </p>
            </div>
            <div className="chat-message">
              <p className="font-medium text-sm">이대리</p>
              <p>
                @김OO 미리 검토 부탁드립니다! 특히 소셜 미디어 부분 의견 주세요.
              </p>
            </div>
            <div className="chat-message sent">
              <p className="font-medium text-sm">김OO</p>
              <p>
                좋은 아침입니다! 오늘 회의 자료 미리 확인했습니다. 소셜 미디어
                전략에 몇 가지 아이디어가 있어요. 📝
              </p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 w-full">
            <h3 className="font-bold text-lg mb-3 text-center text-blue-700">
              Chat 활용 실무 팁
            </h3>
            <div className="flex flex-col space-y-3">
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1">
                  <i className="fas fa-bell text-blue-500 fa-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    알림 맞춤설정
                  </p>
                  <p className="text-xs text-gray-600">
                    중요 스페이스는 높은 알림 우선순위 설정
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1">
                  <i className="fas fa-search text-blue-500 fa-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    검색 기능 활용
                  </p>
                  <p className="text-xs text-gray-600">
                    키워드로 과거 대화내용 빠르게 찾기
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1">
                  <i className="fas fa-mobile-alt text-blue-500 fa-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    모바일 앱 활용
                  </p>
                  <p className="text-xs text-gray-600">
                    이동 중에도 실시간 소통 유지
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">9 / 20</div>
      </div>
    </div>,
    // 열 번째 슬라이드 (오전 10시 내부회의)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex flex-col justify-center items-center py-12 flex-grow">
        {/* 시간 표시 */}
        <div className="circle-icon red-circle mb-8">
          <div className="text-center">
            <span className="text-2xl font-bold">10:00</span>
            <div className="text-sm font-medium mt-1">AM</div>
          </div>
        </div>

        {/* 제목 */}
        <h1 className="title text-5xl mb-6 text-center">
          🌟 STEP 2. 오전 10시 : 비대면 내부회의와 액션플랜
        </h1>

        {/* 구분선 */}
        <div className="w-24 h-1 bg-red-500 mb-8"></div>

        {/* 부제목 */}
        <p className="subtitle text-2xl text-center max-w-3xl mb-10">
          Google Meet의 강력한 협업 기능 &amp; 실시간 Google Docs 협업 회의록
          작성
          <br />
          화면 공유・댓글・할 일 분배 실전
        </p>

        {/* 도구 뱃지 */}
        <div className="mt-4">
          <span className="tool-badge badge-blue">
            <i className="fas fa-video mr-2"></i>Meet
          </span>
          <span className="tool-badge badge-green">
            <i className="fas fa-file-alt mr-2"></i>Docs
          </span>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">10 / 20</div>
      </div>
    </div>,
    // 11번째 슬라이드 (Google Meet 활용)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">Google Meet 활용</h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-1/2 pr-10">
          <div className="meet-box p-6 mb-8">
            <h2 className="font-bold text-xl mb-4">
              캘린더에서 바로 Meet 접속
            </h2>
            <div className="flex items-start mb-4">
              <div className="step-marker">1</div>
              <p className="content text-lg">
                캘린더 일정(10시 회의)을 클릭한 후{" "}
                <span className="highlight">'Google Meet으로 참여'</span> 버튼을
                클릭하여 화상회의에 즉시 입장합니다.
              </p>
            </div>
            <div className="flex items-start mb-4">
              <div className="step-marker">2</div>
              <p className="content text-lg">
                회의 시작 5분 전에 캘린더에서{" "}
                <span className="highlight">자동 알림</span>을 받아 회의 준비가
                가능합니다.
              </p>
            </div>
            <div className="flex items-start">
              <div className="step-marker">3</div>
              <p className="content text-lg">
                캘린더에 <span className="highlight">회의록 문서 링크</span>가
                첨부되어 있다면 회의 전에 미리 확인할 수 있습니다.
              </p>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-3">Meet 화상회의 활용 Tip</h3>
            <ul className="content text-lg space-y-3 pl-2">
              <li>
                <i className="fas fa-check-circle text-green-600 mr-2"></i>{" "}
                중요한 회의는 <span className="highlight">녹화 기능</span>을
                활용하여 추후 참고
              </li>
              <li>
                <i className="fas fa-check-circle text-green-600 mr-2"></i>{" "}
                화상회의 <span className="highlight">배경 흐림/변경</span>{" "}
                기능으로 전문성 유지
              </li>
              <li>
                <i className="fas fa-check-circle text-green-600 mr-2"></i>{" "}
                <span className="highlight">손들기 기능</span>으로 발언 순서
                효율적 관리
              </li>
            </ul>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-1/2 flex flex-col">
          <h2 className="font-bold text-xl mb-6">
            화면 공유로 실시간 발표하기
          </h2>
          <div className="feature-box bg-white p-5 mb-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                <i className="fas fa-desktop text-blue-500 fa-lg"></i>
              </div>
              <h3 className="font-bold text-lg">화면 공유 방법</h3>
            </div>
            <ol className="content text-lg space-y-2 pl-4 list-decimal">
              <li>
                Meet 하단 메뉴에서 <span className="highlight">'발표하기'</span>{" "}
                버튼 클릭
              </li>
              <li>'전체 화면' 또는 '창' 중 선택</li>
              <li>공유할 콘텐츠(Google Slides) 선택 후 '공유'</li>
            </ol>
          </div>
          <div className="feature-box bg-white p-5">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <i className="fas fa-chart-bar text-red-500 fa-lg"></i>
              </div>
              <h3 className="font-bold text-lg">발표 시 주의사항</h3>
            </div>
            <ul className="content text-lg space-y-2 pl-2">
              <li>
                <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>{" "}
                발표 전 <span className="highlight">개인 알림</span> 끄기
              </li>
              <li>
                <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>{" "}
                공유 시 <span className="highlight">발표자 화면</span>만
                공유하기
              </li>
              <li>
                <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>{" "}
                <span className="highlight">다른 참가자 확인</span>을 위해 발표
                중에도 참가자 패널 열어두기
              </li>
            </ul>
          </div>
          <div className="flex justify-center mt-6">
            <div className="px-4 py-2 bg-blue-100 rounded-lg text-blue-800 font-medium text-sm">
              <i className="fas fa-lightbulb mr-2"></i> 마케팅팀 김대리의 실습:
              조사 데이터를 발표하며 화면 공유 기능 시연
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">11 / 20</div>
      </div>
    </div>,
    // 12번째 슬라이드 (Docs 실시간 회의록 & 담당 지정)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          Docs 실시간 회의록 &amp; 담당 지정
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-6/12 pr-10">
          <div className="mb-8">
            <div className="flex items-start mb-4">
              <div className="tag tag-blue">핵심 기능</div>
              <div className="tag tag-red">생산성</div>
              <div className="tag tag-yellow">협업</div>
            </div>
            <h2 className="font-bold text-xl mb-3">
              모두가 동시에 회의록 작성·수정
            </h2>
            <ul className="content text-lg list-disc pl-6 space-y-3">
              <li>
                여러 사람이 <span className="highlight">실시간으로 동시에</span>{" "}
                문서 편집 가능
              </li>
              <li>
                사용자별 <span className="highlight">컬러 커서</span>로 누가
                어디를 편집 중인지 확인
              </li>
              <li>
                별도 저장 필요 없이 <span className="highlight">자동 저장</span>{" "}
                (모든 변경사항 즉시 반영)
              </li>
            </ul>
          </div>
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">
              @멘션, 댓글 활용 &amp; 할 일(Task) 체킹
            </h2>
            <ul className="content text-lg list-disc pl-6 space-y-3">
              <li>
                <span className="highlight">@[이름]</span> 으로 특정 담당자 태그
                및 알림 전송
              </li>
              <li>
                댓글 기능으로{" "}
                <span className="highlight">특정 내용에 피드백</span> 추가
              </li>
              <li>
                중요 작업은{" "}
                <span className="highlight">할 일(Task)로 변환</span>하여 추적
                관리
              </li>
            </ul>
          </div>
          <div>
            <h2 className="font-bold text-xl mb-3">
              실전: 담당자 지정·작업 분배
            </h2>
            <p className="content text-lg mb-2">
              "<span className="highlight">@박과장님</span>, 이 부분 확인
              부탁드립니다"라고 댓글 달기
            </p>
            <p className="content text-lg">
              <span className="highlight">작업 할당</span> 기능으로 본인 담당
              항목 체크리스트화
            </p>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-6/12 flex flex-col justify-between">
          <div className="grid grid-cols-2 gap-4 h-2/3">
            <div className="feature-card bg-white p-4 flex flex-col items-center justify-center border border-gray-100">
              <div className="icon-box blue-box mb-3">
                <i className="fas fa-users-cog fa-2x"></i>
              </div>
              <h3 className="font-medium text-center mb-2">실시간 공동작업</h3>
              <p className="text-sm text-gray-600 text-center">
                여러 팀원이 동시에 문서 편집
              </p>
            </div>
            <div className="feature-card bg-white p-4 flex flex-col items-center justify-center border border-gray-100">
              <div className="icon-box red-box mb-3">
                <i className="fas fa-comments fa-2x"></i>
              </div>
              <h3 className="font-medium text-center mb-2">댓글 &amp; 멘션</h3>
              <p className="text-sm text-gray-600 text-center">
                특정 내용에 피드백, 담당자 태그
              </p>
            </div>
            <div className="feature-card bg-white p-4 flex flex-col items-center justify-center border border-gray-100">
              <div className="icon-box yellow-box mb-3">
                <i className="fas fa-tasks fa-2x"></i>
              </div>
              <h3 className="font-medium text-center mb-2">할 일 지정</h3>
              <p className="text-sm text-gray-600 text-center">
                담당자별 작업 할당 및 추적
              </p>
            </div>
            <div className="feature-card bg-white p-4 flex flex-col items-center justify-center border border-gray-100">
              <div className="icon-box green-box mb-3">
                <i className="fas fa-history fa-2x"></i>
              </div>
              <h3 className="font-medium text-center mb-2">버전 기록</h3>
              <p className="text-sm text-gray-600 text-center">
                모든 변경사항 이력 관리
              </p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-5 mt-4">
            <h3 className="font-bold text-lg mb-3">실무 팁</h3>
            <div className="flex items-start">
              <div className="text-green-500 mr-2">
                <i className="fas fa-lightbulb"></i>
              </div>
              <div className="content text-base">
                회의 시작 전{" "}
                <span className="highlight">문서 템플릿을 미리 준비</span>하고
                참석자에게 공유하면 회의 진행이 더 효율적입니다.
              </div>
            </div>
            <div className="flex items-start mt-2">
              <div className="text-green-500 mr-2">
                <i className="fas fa-lightbulb"></i>
              </div>
              <div className="content text-base">
                댓글에 <span className="highlight">'✓ 해결됨'</span> 처리하여
                완료된 피드백을 관리하세요.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">12 / 20</div>
      </div>
    </div>,
    // 13번째 슬라이드 (오후 1시 문서 작업 및 협업)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex flex-col justify-center items-center py-12 flex-grow">
        {/* 시간 표시 */}
        <div className="circle-icon green-circle mb-8">
          <div className="text-center">
            <span className="text-2xl font-bold">13:00</span>
            <div className="text-sm font-medium mt-1">PM</div>
          </div>
        </div>

        {/* 제목 */}
        <h1 className="title text-5xl mb-6 text-center">
          🌟 STEP 3. 오후 1시 : 문서 작업 &amp; 협업
        </h1>

        {/* 구분선 */}
        <div className="w-24 h-1 bg-green-500 mb-8"></div>

        {/* 부제목 */}
        <p className="subtitle text-2xl text-center max-w-3xl mb-10">
          Drive, Docs, Sheets의 공유·동시작업, 연동 팁까지!
          <br />
          자료 수집-분석-보고서 작성-피드백 실무 흐름 완전 정복
        </p>

        {/* 도구 뱃지 */}
        <div className="mt-4">
          <span className="tool-badge badge-blue">
            <i className="fas fa-folder mr-2"></i>Drive
          </span>
          <span className="tool-badge badge-red">
            <i className="fas fa-file-alt mr-2"></i>Docs
          </span>
          <span className="tool-badge badge-green">
            <i className="fas fa-table mr-2"></i>Sheets
          </span>
          <span className="tool-badge badge-yellow">
            <i className="fas fa-comments mr-2"></i>Chat
          </span>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">13 / 20</div>
      </div>
    </div>,
    // 14번째 슬라이드 (공유드라이브·폴더로 체계적 자료관리)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          공유드라이브·폴더로 체계적 자료관리
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-6/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">
              마케팅팀 공유 드라이브 활용
            </h2>
            <ul className="content text-lg space-y-3 list-disc pl-6">
              <li>
                <span className="highlight">팀 단위 자료 통합 관리</span> - 개인
                드라이브가 아닌 공유 드라이브에 자료를 저장하여{" "}
                <span className="highlight">부서 자산화</span>
              </li>
              <li>
                <span className="highlight">접근 권한 관리</span> - 필요한
                팀원들에게 적절한 권한 부여 (편집자/댓글 작성자/뷰어)
              </li>
              <li>
                <span className="highlight">자동 백업 및 동기화</span> -
                수정사항 실시간 반영, 버전 관리 기능
              </li>
            </ul>
          </div>
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">파일명 규칙 표준화</h2>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-base font-medium">파일명 작성 원칙</div>
              <p className="text-sm text-gray-600">
                [프로젝트명]_[문서유형]_[작성자]_[버전]_[날짜]
              </p>
              <p className="text-sm text-gray-600 mt-2">
                예시:{" "}
                <span className="text-blue-600">
                  [4분기캠페인]_경쟁사분석_김OO_v1.0_230910
                </span>
              </p>
            </div>
            <ul className="content text-base space-y-2">
              <li>파일 식별과 검색을 위한 명확한 규칙 준수</li>
              <li>버전 관리를 위한 숫자 체계 (v1.0, v1.1, v2.0)</li>
            </ul>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-6/12 pl-4 flex flex-col">
          <div className="mb-6 flex items-center">
            <div className="icon-box blue-box mr-6">
              <i className="fas fa-folder-open fa-3x"></i>
            </div>
            <div>
              <h3 className="font-bold text-lg">체계적 폴더 구조 설계</h3>
              <p className="text-gray-600">
                명확한 계층 구조와 일관된 네이밍으로 모든 팀원이 직관적으로 자료
                접근
              </p>
            </div>
          </div>
          <div className="folder-structure flex-grow">
            <div className="folder-item font-medium">
              <i className="fas fa-folder text-blue-500 mr-2"></i> 마케팅팀 공유
              드라이브
            </div>
            <div className="folder-item subfolder">
              <i className="fas fa-folder text-yellow-600 mr-2"></i> 4분기
              캠페인
            </div>
            <div className="file-item">
              <span className="badge blue-badge">기획</span>
              <i className="fas fa-file-alt text-blue-500 mr-2"></i>{" "}
              [4분기캠페인]_기획안_v2.0_230905.docx
            </div>
            <div className="file-item">
              <span className="badge yellow-badge">분석</span>
              <i className="fas fa-file-excel text-green-600 mr-2"></i>{" "}
              [4분기캠페인]_경쟁사분석_v1.0_230910.xlsx
            </div>
            <div className="folder-item subfolder">
              <i className="fas fa-folder text-yellow-600 mr-2"></i> 회의록
            </div>
            <div className="file-item">
              <span className="badge green-badge">회의</span>
              <i className="fas fa-file-alt text-blue-500 mr-2"></i>{" "}
              [4분기캠페인]_주간회의_230912.docx
            </div>
            <div className="folder-item subfolder">
              <i className="fas fa-folder text-yellow-600 mr-2"></i> 디자인 소스
            </div>
            <div className="folder-item subfolder">
              <i className="fas fa-folder text-yellow-600 mr-2"></i> 참고자료
            </div>
          </div>
          <div className="mt-5 bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <i className="fas fa-lightbulb text-yellow-500 mr-2"></i>
              <strong>실무 팁:</strong> 중요한 파일은 별표(⭐)로 표시하여 빠르게
              접근할 수 있습니다. 자주 사용하는 폴더는 '나와 공유됨' 섹션에
              바로가기로 등록하세요.
            </p>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">14 / 20</div>
      </div>
    </div>,
    // 15번째 슬라이드 (Docs/Sheets/Slides 실시간 동시작업과 연동)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          Docs/Sheets/Slides 실시간 동시작업과 연동
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div
            className="mb-6 feature-card"
            style={{ borderLeftColor: "#4285F4", backgroundColor: "#F8FAFF" }}
          >
            <div className="flex items-center mb-2">
              <div className="app-icon docs-color">
                <i className="fas fa-file-alt"></i>
              </div>
              <h2 className="font-bold text-xl">실시간 동시 편집</h2>
            </div>
            <p className="content text-base mb-3">
              여러 팀원이{" "}
              <span className="highlight">동시에 같은 문서를 편집</span>할 수
              있어 시간을 단축하고 효율성을 높입니다.
            </p>
            <div className="flex items-center mb-2">
              <div className="avatar bg-blue-500 mr-2">김</div>
              <div className="avatar bg-red-500 mr-2">이</div>
              <div className="avatar bg-green-500 mr-2">박</div>
              <p className="text-sm text-gray-600">
                각자 맡은 부분을 동시에 작업
              </p>
            </div>
          </div>

          <div
            className="mb-6 feature-card"
            style={{ borderLeftColor: "#0F9D58", backgroundColor: "#F5FBF8" }}
          >
            <div className="flex items-center mb-2">
              <div className="app-icon sheets-color">
                <i className="fas fa-table"></i>
              </div>
              <h2 className="font-bold text-xl">링크된 객체 삽입</h2>
            </div>
            <p className="content text-base">
              Sheets의 차트나 표를 Docs/Slides에{" "}
              <span className="highlight">연결하여 붙여넣기</span>할 수
              있습니다. 원본 데이터가 변경되면 링크된 객체도 업데이트됩니다.
            </p>
            <ul className="content text-base list-disc pl-6 space-y-1 mt-2">
              <li>스프레드시트 → 문서/슬라이드로 차트 연결</li>
              <li>
                원본 데이터 변경 시{" "}
                <span className="highlight">자동 업데이트 옵션</span> 활성화
              </li>
              <li>
                수동 업데이트 시{" "}
                <span className="highlight">'업데이트' 버튼</span> 클릭
              </li>
            </ul>
          </div>

          <div
            className="feature-card"
            style={{ borderLeftColor: "#F4B400", backgroundColor: "#FFFBF1" }}
          >
            <div className="flex items-center mb-2">
              <div className="app-icon slides-color">
                <i className="fas fa-desktop"></i>
              </div>
              <h2 className="font-bold text-xl">자동 동기화 &amp; 변경 내역</h2>
            </div>
            <p className="content text-base mb-3">
              모든 변경사항은{" "}
              <span className="highlight">실시간으로 클라우드에 저장</span>되어
              작업 손실 없이 언제 어디서나 최신 버전에 접근할 수 있습니다.
            </p>
            <div className="flex items-center text-sm text-gray-600">
              <i className="fas fa-history mr-2 text-gray-500"></i>
              <span>모든 변경 내역 보관으로 이전 버전 복원 가능</span>
            </div>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="bg-gray-50 rounded-lg p-6 w-full mb-6">
            <h3 className="font-bold text-lg mb-3 text-center">
              실무 활용 사례
            </h3>
            <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <div
                  className="icon-box blue-box mr-3"
                  style={{ width: "48px", height: "48px" }}
                >
                  <i className="fas fa-file-alt fa-lg"></i>
                </div>
                <div>
                  <h4 className="font-medium">경쟁사 분석 보고서</h4>
                  <p className="text-sm text-gray-600">
                    김대리: 1-2페이지, 이사원: 3-4페이지 동시 작업
                  </p>
                </div>
              </div>
            </div>
            <div className="relative mb-4">
              <div className="w-full h-1 bg-gray-200 absolute top-1/2 transform -translate-y-1/2"></div>
              <div className="flex justify-between relative">
                <div
                  className="icon-box green-box"
                  style={{ width: "48px", height: "48px", zIndex: 10 }}
                >
                  <i className="fas fa-table fa-lg"></i>
                </div>
                <div
                  className="icon-box yellow-box"
                  style={{ width: "48px", height: "48px", zIndex: 10 }}
                >
                  <i className="fas fa-desktop fa-lg"></i>
                </div>
              </div>
              <div className="flex justify-center mt-2">
                <i className="fas fa-sync text-blue-500 fa-lg"></i>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <div
                  className="icon-box green-box mr-3"
                  style={{ width: "48px", height: "48px" }}
                >
                  <i className="fas fa-chart-bar fa-lg"></i>
                </div>
                <div>
                  <h4 className="font-medium">예산 데이터 &amp; 보고서 연동</h4>
                  <p className="text-sm text-gray-600">
                    Sheets 데이터 변경 → Docs 차트 자동 반영
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 w-full mt-4">
            <h3 className="font-medium text-center mb-2">💡 실무 팁</h3>
            <ul className="text-sm space-y-2">
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>링크된 객체 삽입: 복사 후 '편집 → 붙여넣기 → 링크'</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>대용량 데이터는 문서를 분리하여 작업 후 연결</span>
              </li>
              <li className="flex items-start">
                <i className="fas fa-check-circle text-green-500 mt-1 mr-2"></i>
                <span>버전 관리: '파일 → 버전 기록 → 버전 기록 보기'</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">15 / 20</div>
      </div>
    </div>,
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          Chat로 신속 피드백 &amp; 문서 공유
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3">
              이메일 대신 Chat으로 즉시 공유하기
            </h2>
            <p className="content text-lg mb-3">
              중요한 문서 초안이 완성되었을 때, 이메일 대신{" "}
              <span className="highlight">Google Chat</span>으로 링크를 공유하면
              즉각적인 피드백을 얻을 수 있습니다.
            </p>
            <div className="benefit-box">
              <p className="font-medium">Google Chat 문서 공유의 장점</p>
              <ul className="text-sm pl-6 list-disc mt-2">
                <li>메일함이 아닌 대화 흐름 내에서 문서 관리</li>
                <li>수신확인 절차 없이 바로 확인 가능</li>
                <li>1:1 또는 팀 스페이스에 동시 공유 가능</li>
              </ul>
            </div>
          </div>
          <div>
            <h2 className="font-bold text-xl mb-3">효과적인 피드백 요청하기</h2>
            <ul className="content text-lg list-disc pl-6 space-y-2">
              <li>
                <span className="highlight">구체적인 요청</span>: "팀장님,
                보고서 초안 검토 부탁드립니다.{" "}
                <span className="font-medium text-gray-700">
                  특히 3페이지 예산안 부분
                </span>{" "}
                의견 주시면 감사하겠습니다."
              </li>
              <li>
                <span className="highlight">마감 시간 명시</span>: "오늘 오후
                4시까지 피드백 주시면 수정 후 제출하겠습니다."
              </li>
              <li>
                <span className="highlight">댓글 사용 유도</span>: "문서 내
                댓글로 남겨주시면 더 효율적으로 반영할 수 있습니다."
              </li>
            </ul>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center">
          <div className="flex space-x-6 mb-8">
            <div className="icon-box blue-box">
              <i className="fas fa-comments fa-3x"></i>
            </div>
            <div className="icon-box green-box">
              <i className="fas fa-file-alt fa-3x"></i>
            </div>
          </div>

          {/* 채팅 예시 시각화 */}
          <div className="bg-gray-50 rounded-lg p-5 w-full mb-5">
            <p className="text-sm text-gray-500 mb-3 text-center">
              📱 Chat 대화 예시
            </p>
            <div className="flex flex-col">
              <div className="chat-bubble sender-bubble">
                <p className="text-sm font-medium mb-1">김OO</p>
                <p className="text-sm">
                  팀장님, 보고서 초안 검토 부탁드립니다. 특히 3페이지 예산안
                  부분 의견 주시면 감사하겠습니다.
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  <i className="fas fa-link mr-1"></i>
                  4분기_캠페인_보고서_초안.docs
                </p>
              </div>
              <div className="chat-bubble receiver-bubble">
                <p className="text-sm font-medium mb-1">이팀장</p>
                <p className="text-sm">
                  지금 확인해볼게요. 30분 내로 댓글 남기겠습니다.
                </p>
              </div>
              <div className="chat-bubble sender-bubble">
                <p className="text-sm font-medium mb-1">김OO</p>
                <p className="text-sm">
                  감사합니다! 오후 미팅 전까지 수정본 준비하겠습니다.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center bg-blue-50 rounded-lg p-3 w-full">
            <i className="fas fa-lightbulb text-yellow-500 mr-3"></i>
            <p className="text-sm">
              <span className="font-medium">시간 절약 팁:</span> 자주 공유하는
              문서는 Chat 스페이스에 고정하여 팀원들이 언제든 접근할 수 있게
              하세요.
            </p>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">16 / 20</div>
      </div>
    </div>,
    // 17번째 슬라이드 (오후 5시 30분 업무 마무리 및 퇴근 준비)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex flex-col justify-center items-center py-12 flex-grow">
        {/* 시간 표시 */}
        <div className="circle-icon green-circle mb-8">
          <div className="text-center">
            <span className="text-2xl font-bold">17:30</span>
            <div className="text-sm font-medium mt-1">PM</div>
          </div>
        </div>

        {/* 제목 */}
        <h1 className="title text-5xl mb-6 text-center">
          🌟 STEP 4. 오후 5시 30분 : 업무 마무리와 내일 준비
        </h1>

        {/* 구분선 */}
        <div className="w-24 h-1 bg-green-500 mb-8"></div>

        {/* 부제목 */}
        <p className="subtitle text-2xl text-center max-w-3xl mb-10">
          업무 현황 공유, 할 일 체크, 예약 발송 팁으로 스마트 워크 마감하기
        </p>

        {/* 도구 뱃지 */}
        <div className="mt-4">
          <span className="tool-badge badge-yellow">
            <i className="fas fa-comments mr-2"></i>Chat
          </span>
          <span className="tool-badge badge-green">
            <i className="fas fa-tasks mr-2"></i>Tasks
          </span>
          <span className="tool-badge badge-blue">
            <i className="fas fa-envelope mr-2"></i>Gmail
          </span>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">17 / 20</div>
      </div>
    </div>,
    // 18번째 슬라이드 (Google Tasks & Chat 업무 마무리)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          Google Tasks &amp; Chat로 업무 마무리
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex px-16 py-4 flex-grow">
        {/* 왼쪽 콘텐츠 영역 */}
        <div className="w-7/12 pr-10">
          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3 flex items-center">
              <i className="fas fa-tasks blue-icon mr-2"></i>
              <span>내일의 할 일 관리 (Google Tasks)</span>
            </h2>
            <ul className="content text-lg list-disc pl-6 space-y-2">
              <li>
                Gmail 우측의 <span className="highlight">Tasks</span> 패널에서
                내일 해야 할 일 등록
              </li>
              <li>
                항목별로 <span className="highlight">마감일, 세부정보</span>{" "}
                추가 가능
              </li>
              <li>완료 체크박스로 진행 상황 쉽게 관리</li>
              <li>
                예: '광고대행사 전달 자료 최종 확인', '주간 보고 메일 발송'
              </li>
            </ul>
          </div>

          <div className="mb-8">
            <h2 className="font-bold text-xl mb-3 flex items-center">
              <i className="fas fa-comment-alt red-icon mr-2"></i>
              <span>업무 진행 현황 공유 (Google Chat)</span>
            </h2>
            <ul className="content text-lg list-disc pl-6 space-y-2">
              <li>
                '4분기 캠페인 프로젝트'{" "}
                <span className="highlight">스페이스</span>에 업무 요약 공유
              </li>
              <li>
                간결하게{" "}
                <span className="highlight">
                  진행상황, 완료 항목, 예정 작업
                </span>{" "}
                정리
              </li>
              <li>
                예: "금일 업무: 경쟁사 분석 보고서 초안 작성 완료. 내일 오전 중
                최종본 공유 예정"
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-bold text-xl mb-3 flex items-center">
              <i className="fas fa-paper-plane yellow-icon mr-2"></i>
              <span>이메일 예약 발송 (Gmail)</span>
            </h2>
            <ul className="content text-lg list-disc pl-6 space-y-2">
              <li>
                '보내기' 버튼 옆 드롭다운에서{" "}
                <span className="highlight">예약 발송</span> 선택
              </li>
              <li>날짜와 시간을 지정하여 최적의 시점에 자동 발송</li>
              <li>업무 시간 외 준비 완료된 메일을 다음 날 아침에 발송</li>
              <li>예: 내일 오전 9시 광고대행사 전달 자료 예약 발송</li>
            </ul>
          </div>
        </div>

        {/* 오른쪽 이미지/아이콘 영역 */}
        <div className="w-5/12 flex flex-col justify-center items-center space-y-4">
          {/* Tasks 카드 */}
          <div className="feature-card bg-blue-50 p-6 w-full">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <i className="fas fa-check-square text-blue-500"></i>
              </div>
              <h3 className="font-bold text-lg">
                Tasks - 내일을 준비하는 체크리스트
              </h3>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center mb-2">
                <div className="w-5 h-5 border border-blue-400 rounded flex items-center justify-center mr-2">
                  <i className="fas fa-check text-blue-500 text-xs"></i>
                </div>
                <span>광고대행사 전달 자료 최종 확인</span>
              </div>
              <div className="flex items-center">
                <div className="w-5 h-5 border border-gray-300 rounded mr-2"></div>
                <span>주간 보고 메일 발송</span>
              </div>
            </div>
          </div>

          {/* Chat 카드 */}
          <div className="feature-card bg-red-50 p-6 w-full">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <i className="fas fa-comments text-red-500"></i>
              </div>
              <h3 className="font-bold text-lg">Chat - 팀과의 투명한 소통</h3>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex">
                <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center mr-2 flex-shrink-0">
                  <span className="text-xs font-bold">김</span>
                </div>
                <div>
                  <p className="text-sm mb-1">
                    <span className="font-bold">김OO</span> · 오후 5:30
                  </p>
                  <p className="text-sm">
                    금일 업무: 경쟁사 분석 보고서 초안 작성 완료. 내일 오전 중
                    최종본 공유 예정
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 예약 발송 카드 */}
          <div className="feature-card bg-yellow-50 p-6 w-full">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                <i className="fas fa-clock text-yellow-600"></i>
              </div>
              <h3 className="font-bold text-lg">
                Gmail 예약 발송 - 자동화의 시작
              </h3>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm flex items-center">
              <i className="fas fa-paper-plane text-yellow-500 mr-3"></i>
              <div className="text-sm">
                <p className="font-medium">내일 오전 9:00에 발송 예약됨</p>
                <p className="text-gray-500">광고대행사 4분기 캠페인 자료</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">18 / 20</div>
      </div>
    </div>,
    // 19번째 슬라이드 (학습 포인트 총정리)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">학습 포인트 총정리 &amp; 실전팁</h1>
        <div className="w-16 h-1 bg-blue-500 mb-6"></div>
        <p className="content text-lg mb-4">
          Google Workspace 도구들을 유기적으로 활용하여 출근부터 퇴근까지
          스마트한 업무 흐름을 만드는 14가지 핵심 포인트
        </p>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="px-16 py-2 flex-grow">
        {/* 오전 9시 영역 */}
        <div className="mb-4">
          <div className="time-badge">
            <i className="far fa-clock mr-1"></i> STEP 1: 오전 9시 - 출근 및
            업무 시작
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="learning-point bg-red-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon gmail-icon">
                  <i className="fas fa-envelope fa-sm"></i>
                </span>
                <span className="font-medium">Gmail 라벨 활용</span>
              </div>
              <p className="text-sm content ml-8">
                '[4분기 캠페인]', '[거래처]' 등 자동 분류로 중요 메일 놓치지
                않기
              </p>
            </div>
            <div className="learning-point bg-red-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon gmail-icon">
                  <i className="fas fa-envelope fa-sm"></i>
                </span>
                <span className="font-medium">Gmail 다시 알림</span>
              </div>
              <p className="text-sm content ml-8">
                바로 처리하기 어려운 메일은 '오후 2시', '내일 오전'으로 설정
              </p>
            </div>
            <div className="learning-point bg-blue-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon calendar-icon">
                  <i className="far fa-calendar fa-sm"></i>
                </span>
                <span className="font-medium">캘린더 일정 확인</span>
              </div>
              <p className="text-sm content ml-8">
                오늘 예정된 '10시 주간회의'와 '3시 미팅' 일정 파악하기
              </p>
            </div>
            <div className="learning-point bg-blue-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon calendar-icon">
                  <i className="far fa-calendar fa-sm"></i>
                </span>
                <span className="font-medium">회의 첨부파일 확인</span>
              </div>
              <p className="text-sm content ml-8">
                일정에 첨부된 회의록, 안건을 미리 열람해 사전 준비
              </p>
            </div>
          </div>
        </div>

        {/* 오전 10시 영역 */}
        <div className="mb-4">
          <div className="time-badge">
            <i className="far fa-clock mr-1"></i> STEP 2: 오전 10시 - 내부 회의
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="learning-point bg-blue-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon meet-icon">
                  <i className="fas fa-video fa-sm"></i>
                </span>
                <span className="font-medium">Meet 화면 공유</span>
              </div>
              <p className="text-sm content ml-8">
                조사 자료와 Google Slides를 발표하며 시각적 소통
              </p>
            </div>
            <div className="learning-point bg-blue-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon docs-icon">
                  <i className="far fa-file-alt fa-sm"></i>
                </span>
                <span className="font-medium">Docs 실시간 회의록</span>
              </div>
              <p className="text-sm content ml-8">
                전 팀원이 동시에 회의 내용을 실시간으로 작성/수정
              </p>
            </div>
            <div className="learning-point bg-blue-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon docs-icon">
                  <i className="far fa-file-alt fa-sm"></i>
                </span>
                <span className="font-medium">댓글 및 할일 할당</span>
              </div>
              <p className="text-sm content ml-8">
                @멘션으로 담당자 태그하고 작업 체크리스트 만들기
              </p>
            </div>
          </div>
        </div>

        {/* 오후 1시 영역 */}
        <div className="mb-4">
          <div className="time-badge">
            <i className="far fa-clock mr-1"></i> STEP 3: 오후 1시 - 문서 작업
            및 협업
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="learning-point bg-yellow-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon drive-icon">
                  <i className="fas fa-hdd fa-sm"></i>
                </span>
                <span className="font-medium">공유 드라이브 활용</span>
              </div>
              <p className="text-sm content ml-8">
                '마케팅팀 공유 드라이브'에 자료 정리하여 팀원 모두 접근 가능
              </p>
            </div>
            <div className="learning-point bg-green-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon sheets-icon">
                  <i className="fas fa-table fa-sm"></i>
                </span>
                <span className="font-medium">Sheets &amp; Docs 연동</span>
              </div>
              <p className="text-sm content ml-8">
                차트를 '연결하여 붙여넣기'로 데이터 변경 시 자동 업데이트
              </p>
            </div>
            <div className="learning-point bg-green-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon docs-icon">
                  <i className="far fa-file-alt fa-sm"></i>
                </span>
                <span className="font-medium">실시간 동시 작업</span>
              </div>
              <p className="text-sm content ml-8">
                여러 팀원이 서로 다른 파트를 동시에 작업하여 시간 단축
              </p>
            </div>
            <div className="learning-point bg-green-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon chat-icon">
                  <i className="far fa-comment fa-sm"></i>
                </span>
                <span className="font-medium">Chat 문서 링크 공유</span>
              </div>
              <p className="text-sm content ml-8">
                이메일 대신 Chat으로 문서 링크를 보내 즉각적 피드백 유도
              </p>
            </div>
          </div>
        </div>

        {/* 오후 5시 30분 영역 */}
        <div>
          <div className="time-badge">
            <i className="far fa-clock mr-1"></i> STEP 4: 오후 5시 30분 - 업무
            마무리
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="learning-point bg-green-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon chat-icon">
                  <i className="far fa-comment fa-sm"></i>
                </span>
                <span className="font-medium">업무 요약 공유</span>
              </div>
              <p className="text-sm content ml-8">
                프로젝트 스페이스에 오늘의 진행상황을 간략히 공유
              </p>
            </div>
            <div className="learning-point bg-blue-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon tasks-icon">
                  <i className="fas fa-tasks fa-sm"></i>
                </span>
                <span className="font-medium">Tasks 할 일 관리</span>
              </div>
              <p className="text-sm content ml-8">
                내일 해야 할 일을 미리 Google Tasks에 등록하여 계획
              </p>
            </div>
            <div className="learning-point bg-red-50 p-3">
              <div className="flex items-center mb-1">
                <span className="tool-icon gmail-icon">
                  <i className="fas fa-envelope fa-sm"></i>
                </span>
                <span className="font-medium">Gmail 예약 발송</span>
              </div>
              <p className="text-sm content ml-8">
                '보내기 예약'으로 내일 오전 9시에 메일이 자동 발송되도록 설정
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 실전 팁 영역 */}
      <div className="px-16 py-2 mb-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-bold text-lg mb-2">📌 실전 활용 팁</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-sm content">
              <i className="fas fa-lightbulb text-yellow-500 mr-1"></i>
              <span className="font-medium">모바일 연동</span>: 이동 중에도 모든
              기능을 스마트폰에서 동일하게 사용
            </div>
            <div className="text-sm content">
              <i className="fas fa-lightbulb text-yellow-500 mr-1"></i>
              <span className="font-medium">단축키 활용</span>: 자주 쓰는 기능은
              단축키로 더 빠르게 작업
            </div>
            <div className="text-sm content">
              <i className="fas fa-lightbulb text-yellow-500 mr-1"></i>
              <span className="font-medium">워크스페이스 연동</span>: 모든
              도구가 하나로 연결되는 통합 환경 활용
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">19 / 20</div>
      </div>
    </div>,
    // 20번째 슬라이드 (Google Workspace 스마트워크의 가치 & Q&A)
    <div className="slide-container flex flex-col">
      {/* 상단 액센트 */}
      <div className="top-accent"></div>

      {/* 컬러 바 */}
      <div className="color-bar">
        <div className="color-blue flex-1"></div>
        <div className="color-red flex-1"></div>
        <div className="color-yellow flex-1"></div>
        <div className="color-green flex-1"></div>
      </div>

      {/* 헤더 */}
      <div className="px-16 pt-10">
        <h1 className="title text-3xl mb-4">
          Google Workspace 스마트워크의 가치 &amp; Q&amp;A
        </h1>
        <div className="w-16 h-1 bg-blue-500 mb-8"></div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="px-16 py-4 flex-grow">
        {/* 핵심 가치 카드 영역 */}
        <div className="flex justify-between mb-10">
          {/* 가치 1: 도구의 연결성 */}
          <div className="value-card bg-white p-6 w-1/3 mr-6">
            <div className="flex items-center mb-4">
              <div className="icon-circle blue-circle mr-4">
                <i className="fas fa-link fa-lg"></i>
              </div>
              <h3 className="text-xl font-bold">도구의 연결성</h3>
            </div>
            <p className="text-sm">
              단순한 여러 앱의 모음이 아닌 하나의 통합된 플랫폼으로 정보 분산을
              막고 업무 흐름을 원활하게 합니다.
            </p>
          </div>

          {/* 가치 2: 시간 절약 */}
          <div className="value-card bg-white p-6 w-1/3 mr-6">
            <div className="flex items-center mb-4">
              <div className="icon-circle red-circle mr-4">
                <i className="fas fa-clock fa-lg"></i>
              </div>
              <h3 className="text-xl font-bold">시간 절약</h3>
            </div>
            <p className="text-sm">
              이메일 관리, 회의록 작성, 문서 공유 등의 반복 작업을 자동화하고
              간소화하여 핵심 업무에 집중할 수 있습니다.
            </p>
          </div>

          {/* 가치 3: 생산성 향상 */}
          <div className="value-card bg-white p-6 w-1/3">
            <div className="flex items-center mb-4">
              <div className="icon-circle yellow-circle mr-4">
                <i className="fas fa-chart-line fa-lg"></i>
              </div>
              <h3 className="text-xl font-bold">생산성 향상</h3>
            </div>
            <p className="text-sm">
              실시간 협업, 동시 편집, 자동 저장 등의 기능으로 팀워크를 강화하고
              결과물 산출 속도를 높입니다.
            </p>
          </div>
        </div>

        {/* Q&A 섹션 */}
        <div className="mt-6">
          <h3 className="font-bold text-xl mb-4">
            <i className="fas fa-question-circle text-blue-500 mr-2"></i>
            질의응답 시간
          </h3>

          {/* 질문 예시 박스 */}
          <div className="question-box p-4 mb-6">
            <h4 className="font-bold mb-2">자주 묻는 질문</h4>
            <ul className="space-y-2 pl-6 list-disc">
              <li>
                Google Workspace를 기존 업무 시스템과 어떻게 연동할 수 있나요?
              </li>
              <li>
                기존 문서를 Google Drive로 효율적으로 이전하는 방법은
                무엇인가요?
              </li>
              <li>Gmail 라벨과 필터 설정을 팀 전체에 적용할 수 있나요?</li>
              <li>
                Google Chat과 Meet을 활용한 효과적인 원격 회의 방법이
                궁금합니다.
              </li>
            </ul>
          </div>

          {/* 토론 및 질문 유도 */}
          <div className="bg-blue-50 p-5 rounded-lg">
            <div className="flex items-start">
              <div className="mr-4 mt-1">
                <i className="fas fa-lightbulb text-2xl text-yellow-500"></i>
              </div>
              <div>
                <h4 className="font-bold mb-2">
                  오늘 배운 내용을 실무에 어떻게 적용하시겠습니까?
                </h4>
                <p className="text-sm mb-2">
                  김OO 대리의 하루 시나리오를 바탕으로, 여러분의 업무 환경에서
                  적용할 수 있는 부분을 공유해주세요.
                </p>
                <p className="text-sm font-medium text-blue-600">
                  질문이나 의견을 자유롭게 나눠주세요!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 페이지 번호 */}
      <div className="px-16 py-4 flex justify-end">
        <div className="page-number">20 / 20</div>
      </div>
    </div>,
  ];

  // 슬라이드 네비게이션
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? prev : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? prev : prev - 1));
  };

  return (
    <div
      className={`education-presentation ${
        isFullscreen ? "fullscreen-mode" : ""
      }`}
      ref={presentationRef}
    >
      {/* 슬라이드 내비게이션 버튼 */}
      <div
        className={`slide-navigation ${isFullscreen ? "fullscreen-nav" : ""}`}
      >
        {/* 전체화면 버튼 추가 */}
        <button className="fullscreen-button" onClick={toggleFullscreen}>
          {isFullscreen ? (
            <>
              <i className="fas fa-compress"></i> 전체화면 종료
            </>
          ) : (
            <>
              <i className="fas fa-expand"></i> 전체화면
            </>
          )}
        </button>
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className={`nav-button ${currentSlide === 0 ? "disabled" : ""}`}
        >
          이전
        </button>

        <span className="slide-counter">
          <span className="px-3">
            {currentSlide + 1} / {slides.length}
          </span>
        </span>

        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className={`nav-button ${
            currentSlide === slides.length - 1 ? "disabled" : ""
          }`}
        >
          다음
        </button>
      </div>

      {slides[currentSlide]}

      {/* 전체화면 모드 정보 표시 */}
      {isFullscreen && (
        <div className="fullscreen-info">
          <p>
            방향키 ← → 로 슬라이드 이동 | F키: 전체화면 전환 | ESC: 전체화면
            종료
          </p>
        </div>
      )}
    </div>
  );
};

export default Education;
